import type { Localized } from './notice'

// --- ACTIVITY REGISTRATIONS -------------------------------------------------

/** A condition a citizen must satisfy before a registration is accepted. */
export interface Prerequisite {
  id: string
  kind: 'age' | 'level' | 'residency' | 'document' | 'activity'
  label: Localized
  /** Shown when a registration fails this condition. */
  detail: Localized
  blocking: boolean
}

export type ActivityStatus = 'open' | 'waitlist' | 'full' | 'closed'

/** One day-and-time an activity meets. An activity can have several. */
export interface ActivitySlot {
  id: string
  /** 1 = Monday … 7 = Sunday. */
  weekday: number
  startTime: string
  endTime: string
  /** Set on one-time activities only, e.g. "2026-04-18". */
  date?: string
}

export interface Activity {
  id: string
  /** Discipline the activity belongs to, e.g. "Natation". */
  type: Localized
  name: Localized
  venue: Localized
  /** Weekly activities repeat all season; one-time ones happen once. */
  recurrence: 'weekly' | 'once'
  slots: ActivitySlot[]
  season: Localized
  ageRange: string
  capacity: number
  registered: number
  waitlisted: number
  priceResident: number
  priceNonResident: number
  status: ActivityStatus
  /** Whether citizens can see the activity in the bciti+ catalogue. */
  visible: boolean
  prerequisites: Prerequisite[]
  /** Staff and equipment the activity needs, beyond its venue. */
  resources?: ActivityResource[]
}

/** A staff member or piece of equipment an activity depends on. */
export interface ActivityResource {
  /** Id of the matching entry in RESOURCES. */
  resourceId: string
  /** How many units are needed — always 1 for a person. */
  quantity: number
}

/** Someone enrolled in — or waiting for — an activity. */
export interface Registrant {
  id: string
  activityId: string
  name: string
  age: number
  guardian?: string
  resident: boolean
  status: 'confirmed' | 'waitlist'
  /** Slot the person attends, when the activity has several. */
  slotId?: string
  paid: boolean
  /** Richer payment record; falls back to `paid` when absent. */
  payment?: Payment
  registeredOn: string
  /** Outstanding prerequisite, when one is missing. */
  missing?: Localized
}

// --- DAY CAMPS --------------------------------------------------------------

export type FileStatus = 'complete' | 'incomplete' | 'missing'

export interface CampRegistration {
  id: string
  childName: string
  childAge: number
  guardian: string
  campName: Localized
  weeks: number
  /** Health form: allergies, medication, emergency contacts. */
  healthForm: FileStatus
  /** Quebec RL-24 tax slip for childcare expenses. */
  rl24: 'issued' | 'pending' | 'not_eligible'
  /** Flags that require a coordinator's attention. */
  alerts: Localized[]
  paid: boolean
  /** Richer payment record; falls back to `paid` when absent. */
  payment?: Payment
  amount: number
}

/** Normalizes the legacy boolean into the three-state payment model. */
export function resolvePayment(record: {
  paid: boolean
  payment?: Payment
}): Payment {
  return (
    record.payment ?? {
      method: 'comptoir',
      status: record.paid ? 'paye' : 'impaye',
    }
  )
}

export interface Camp {
  id: string
  name: Localized
  venue: Localized
  ageRange: string
  weeks: number
  capacity: number
  registered: number
  healthFormsComplete: number
  rl24Issued: number
}

// --- FACILITY & EQUIPMENT BOOKINGS ------------------------------------------

export type RequesterKind = 'organization' | 'citizen'
export type BookingStatus = 'confirmed' | 'pending' | 'refused'

export interface Facility {
  id: string
  name: Localized
  kind: Localized
  capacity: number
  /** Hourly rate charged to citizens; organizations are often exempt. */
  rateCitizen: number
  rateOrganization: number
  /** Percentage of bookable hours already taken this week. */
  occupancy: number
}

export interface Booking {
  id: string
  facility: Localized
  requester: string
  requesterKind: RequesterKind
  /** ISO date of the booking. */
  date: string
  startTime: string
  endTime: string
  purpose: Localized
  status: BookingStatus
  amount: number
  /** Set when the slot overlaps another request. */
  conflict?: Localized
}

export interface Equipment {
  id: string
  name: Localized
  category: Localized
  total: number
  available: number
  onLoan: number
  /** Organizations only — equipment is not lent to individuals. */
  deposit: number
}

export interface EquipmentLoan {
  id: string
  equipment: Localized
  organization: string
  quantity: number
  outDate: string
  dueDate: string
  status: 'out' | 'returned' | 'overdue'
}

// --- ACCESS CONTROL ---------------------------------------------------------

export type AccessResult = 'granted' | 'denied'

export interface AccessEvent {
  id: string
  time: string
  citizen: string
  cardId: string
  /** Photo on file with the citizen card, shown for visual identity check. */
  photo: string
  /** Access point that read the card — used to filter the log. */
  accessPointId: string
  venue: Localized
  /** Pass or entitlement used, e.g. "Abonnement annuel". */
  pass: Localized
  result: AccessResult
  reason?: Localized
}

export interface AccessPoint {
  id: string
  name: Localized
  venue: Localized
  online: boolean
  /** Current headcount inside, against the safe maximum. */
  occupancy: number
  maxOccupancy: number
  entriesToday: number
  deniedToday: number
}

// --- RESOURCES --------------------------------------------------------------

/** Anything that can be reserved: a venue, a piece of gear, a person, a slot. */
export type ResourceKind = 'plateau' | 'equipement' | 'humain' | 'enligne'

export interface Resource {
  id: string
  /** Set on child resources — a room inside a building, for instance. */
  parentId?: string
  name: Localized
  kind: ResourceKind
  address?: string
  capacity?: number
  /** Hourly rate charged to citizens. */
  rateCitizen?: number
  rateOrganization?: number
  /** Percentage of bookable hours already taken this month. */
  occupancy?: number
  bookable: boolean
  /**
   * Who may book this resource. Empty or undefined means both organisations
   * and citizens; an explicit list restricts it.
   */
  bookableBy?: ('organisation' | 'citoyen')[]
  /**
   * Usage types this resource may be booked for. Undefined means every usage
   * type is allowed.
   */
  allowedUsageTypeIds?: string[]
}

// --- USAGE TYPES ------------------------------------------------------------

/**
 * Why a resource is being used. Drives the calendar colour, the default
 * contract, the pricing and whether the slot shows on public calendars.
 */
export interface UsageType {
  id: string
  label: Localized
  /** Hex used for the calendar pill. */
  color: string
  defaultContractTemplateId?: string
  defaultPricing: 'gratuit' | 'tarif'
  showOnPublicCalendar: boolean
}

// --- CONTRACTS --------------------------------------------------------------

export type ContractStatus = 'a_generer' | 'envoye' | 'signe' | 'en_retard'

export interface Contract {
  id: string
  number: string
  templateId: string
  title: string
  locataire: string
  status: ContractStatus
  signedBy?: string
  signedOn?: string
  /** Contracts drafted by the clerk's office are imported, not generated. */
  source: 'plateforme' | 'greffe'
  free: boolean
}

export interface ContractTemplate {
  id: string
  name: Localized
  /** Kind of rental the template covers. */
  scope: Localized
  author: 'loisirs' | 'greffe'
  eSignature: boolean
  clauses: number
}

// --- PAYMENTS ---------------------------------------------------------------

export type PaymentMethod = 'credit' | 'interac' | 'comptoir' | 'exempt'
export type PaymentStatus = 'paye' | 'virement_attendu' | 'impaye' | 'rembourse'

export interface Payment {
  method: PaymentMethod
  status: PaymentStatus
  reference?: string
  paidOn?: string
}

// --- BOOKING FILES ----------------------------------------------------------

export type BookingFileStatus = 'brouillon' | 'active' | 'annulee' | 'facturee'

export interface BookingClient {
  id: string
  name: string
  kind: 'organisation' | 'citoyen'
  phone: string
}

/** One occurrence of a booking. A recurring booking generates many. */
export interface BookingSlot {
  id: string
  date: string
  startTime: string
  endTime: string
}

export interface BookingFile {
  id: string
  number: number
  client: BookingClient
  attentionOf?: string
  resourceId: string
  usageTypeId: string
  startDate: string
  endDate: string
  billingDate?: string
  slots: BookingSlot[]
  status: BookingFileStatus
  author: string
  noteResponsable?: string
  noteFacture?: string
  contractId?: string
  attachments: { name: string; size: string }[]
  billing: { total: number; invoiced: number; paid: number; balance: number }
  payment: Payment
  /** Hidden from public calendars when false. */
  publicCalendar: boolean
}
